import { ref, watch, onMounted } from 'vue'
import { cashRegisterService } from '@/services/cashRegisterService'
import { useToast } from '@/composables/useToast'

export interface Report {
  session_number: number
  session_id: number
  total_transactions: number
  opened_at: string
  closed_at?: string
  total_sales?: number
  total_expenses?: number
  net_cash_flow?: number
}

export interface Period {
  value: string
  label: string
}

export function useReports() {
  const toast = useToast()
  
  // State
  const viewMode = ref<'cards' | 'list'>('cards')
  const startDate = ref('')
  const endDate = ref('')
  const selectedPeriod = ref('week')
  const customStartDate = ref('')
  const customEndDate = ref('')
  const loading = ref(false)
  const dailyReports = ref<Report[]>([])
  const sessionDetailsModalOpen = ref(false)
  const selectedSessionId = ref<number | null>(null)

  // Period options for date selector
  const periods: Period[] = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'custom', label: 'Personalizado' }
  ]

  // Load view mode from localStorage
  const loadViewMode = () => {
    const saved = localStorage.getItem('cash-register-view-mode')
    // On mobile, always default to cards view since toggle is hidden
    const isMobile = window.innerWidth < 640 // sm breakpoint
    if (isMobile) {
      viewMode.value = 'cards'
    } else if (saved === 'cards' || saved === 'list') {
      viewMode.value = saved
    }
  }

  // Save view mode to localStorage when it changes
  watch(viewMode, (newMode) => {
    localStorage.setItem('cash-register-view-mode', newMode)
  }, { immediate: false })

  // Load reports data
  const loadReports = async () => {
    loading.value = true
    try {
      const response = await cashRegisterService.getDailySummaries(startDate.value, endDate.value)
      dailyReports.value = Array.isArray(response) ? response : response.data || []
    } catch (error: any) {
      console.error('Error loading reports:', error)
      toast.showToast(error.response?.data?.detail || 'Failed to load reports', 'error')
    } finally {
      loading.value = false
    }
  }

  // Handle period change
  const handlePeriodChange = (period: string) => {
    selectedPeriod.value = period

    if (period !== 'custom') {
      const today = new Date()
      endDate.value = today.toISOString().split('T')[0]

      switch (period) {
        case 'today':
          startDate.value = today.toISOString().split('T')[0]
          break
        case 'week':
          const lastWeek = new Date(today)
          lastWeek.setDate(today.getDate() - 7)
          startDate.value = lastWeek.toISOString().split('T')[0]
          break
        case 'month':
          const lastMonth = new Date(today)
          lastMonth.setMonth(today.getMonth() - 1)
          startDate.value = lastMonth.toISOString().split('T')[0]
          break
      }

      loadReports()
    }
  }

  // Apply custom date range
  const applyCustomDateRange = () => {
    startDate.value = customStartDate.value
    endDate.value = customEndDate.value
    loadReports()
  }

  // View session details
  const viewSessionDetails = (sessionId: number) => {
    selectedSessionId.value = sessionId
    sessionDetailsModalOpen.value = true
  }

  // Close session details modal
  const closeSessionDetailsModal = () => {
    sessionDetailsModalOpen.value = false
    selectedSessionId.value = null
  }

  // Initialize on mount
  onMounted(() => {
    loadViewMode()
    handlePeriodChange('week')
  })

  return {
    // State
    viewMode,
    startDate,
    endDate,
    selectedPeriod,
    customStartDate,
    customEndDate,
    loading,
    dailyReports,
    sessionDetailsModalOpen,
    selectedSessionId,
    periods,
    
    // Methods
    loadReports,
    handlePeriodChange,
    applyCustomDateRange,
    viewSessionDetails,
    closeSessionDetailsModal,
    loadViewMode
  }
}
